/**
 * A prize, as a list of currency/amount pairs.
 *
 * Mirrors `src/common/event-rewards.js` on the API side. A prize used to be two
 * fixed fields, `reward_px` and `reward_usdt`, which quietly ruled out every
 * other currency — so the screens faked it by writing the coin symbol into
 * `prize_text` and reading it back out with a regex. The API stores the real
 * list now, and this module is the one place that turns a prize row into it.
 *
 * Both storage shapes stay valid:
 *
 *   - `rewards`, an array — the real answer, any currency
 *   - `reward_px` / `reward_usdt` — the original pair, still sent because
 *     finalize credits PX automatically from a column
 *
 * A tier saved before the API gained `rewards` has only the pair, so the list is
 * derived from it. Nothing needs backfilling.
 */

/**
 * Currencies the API credits into the user's balance when an admin approves
 * them. Everything else is a manual transfer recorded with a transaction id.
 */
export const AUTO_CREDITED = ["PX"];

/** Normalise a currency code the way it is displayed and compared. */
export const normaliseCurrency = (value) => String(value || "").trim().toUpperCase();

/** Is this currency one the API credits on its own at finalize? */
export const isAutoCredited = (currency) => AUTO_CREDITED.includes(normaliseCurrency(currency));

/**
 * Read a `rewards` value off an API row.
 *
 * The admin API sends an array, but MariaDB keeps JSON as text, so a string is
 * accepted too. Anything unusable reads as empty rather than throwing — a
 * malformed prize should render as "no prize", not blank the whole ladder.
 */
export const parseRewards = (value) => {
  if (!value) return [];

  let raw = value;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => ({
      currency: normaliseCurrency(entry && entry.currency),
      amount: Number(entry && entry.amount),
    }))
    .filter((entry) => entry.currency && Number.isFinite(entry.amount) && entry.amount > 0);
};

/**
 * What currency a legacy tier's non-PX amount was really in.
 *
 * Before `rewards` existed there was nowhere to put a currency, so a prize of
 * BTC was stored as an amount in `reward_usdt` with its real symbol only in the
 * display text — "0.5 BTC + 100 PX". This is the same reading the screens did
 * before, kept because the editor writes back what it reads: calling that row
 * USDT would not only show the wrong coin, it would save the wrong coin the
 * next time anyone touched the tier.
 *
 * Only consulted when the row has no stored `rewards`.
 */
const legacyCoinCurrency = (row) => {
  const match = String((row && row.prize_text) || "").match(/([0-9.,]+)\s*([A-Za-z0-9_]+)\s*\+/);
  const symbol = match && normaliseCurrency(match[2]);
  return symbol && symbol !== "PX" ? symbol : "USDT";
};

/**
 * The prize list for a prize tier or a winner row.
 *
 * Prefers the stored list; falls back to the two legacy fields. A prize worth
 * nothing is an empty array, not `[{PX, 0}]`.
 *
 * @param {{rewards?:*, reward_px?:*, reward_usdt?:*, prize_text?:string}} row
 * @returns {Array<{currency:string, amount:number}>}
 */
export const rewardsOf = (row) => {
  if (!row) return [];

  const stored = parseRewards(row.rewards);
  if (stored.length > 0) return stored;

  return [
    { currency: "PX", amount: Number(row.reward_px) || 0 },
    { currency: legacyCoinCurrency(row), amount: Number(row.reward_usdt) || 0 },
  ].filter((entry) => entry.amount > 0);
};

/**
 * Turn the editor's rows into what gets sent.
 *
 * Duplicate currencies are summed — two "PX" rows are one prize of their total,
 * not a silent choice between them. `reward_px` / `reward_usdt` come back too,
 * for readable optimistic state; the API derives its own from `rewards` and
 * ignores whatever is sent for them.
 *
 * @returns {{rewards: Array, reward_px: number, reward_usdt: number}}
 */
export const buildRewards = (input) => {
  const merged = new Map();

  for (const entry of Array.isArray(input) ? input : []) {
    const currency = normaliseCurrency(entry && entry.currency);
    const amount = Number(entry && entry.amount);
    if (!currency || !Number.isFinite(amount) || amount <= 0) continue;
    merged.set(currency, (merged.get(currency) || 0) + amount);
  }

  return {
    rewards: [...merged.entries()].map(([currency, amount]) => ({ currency, amount })),
    reward_px: merged.get("PX") || 0,
    reward_usdt: merged.get("USDT") || 0,
  };
};

/** A one-line description — "500 PX + $50 USDT", the way the screens read. */
export const rewardsToText = (rewards) => {
  const list = Array.isArray(rewards) ? rewards : [];
  if (list.length === 0) return "";

  return list
    .map(({ currency, amount }) =>
      normaliseCurrency(currency) === "USDT"
        ? `$${Number(amount).toLocaleString()} USDT`
        : `${Number(amount).toLocaleString()} ${normaliseCurrency(currency)}`
    )
    .join(" + ");
};

/** One reward's amount, for a currency the editor keeps in its own field. */
export const amountOf = (rewards, currency) => {
  const wanted = normaliseCurrency(currency);
  const found = (Array.isArray(rewards) ? rewards : []).find(
    (entry) => normaliseCurrency(entry.currency) === wanted
  );
  return found ? Number(found.amount) || 0 : 0;
};

/** Everything except the named currency, keeping order. */
export const rewardsExcept = (rewards, currency) => {
  const skip = normaliseCurrency(currency);
  return (Array.isArray(rewards) ? rewards : []).filter(
    (entry) => normaliseCurrency(entry.currency) !== skip
  );
};

/**
 * Per-currency totals across every tier, for the "Prize Pool Total" box.
 * A summary cannot add BTC to USDT, so it does not try.
 *
 * @returns {Array<{currency:string, amount:number}>} PX first, then by size
 */
export const totalRewards = (rows) => {
  const totals = new Map();

  for (const row of Array.isArray(rows) ? rows : []) {
    for (const { currency, amount } of rewardsOf(row)) {
      totals.set(currency, (totals.get(currency) || 0) + amount);
    }
  }

  return [...totals.entries()]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => {
      if (a.currency === "PX") return -1;
      if (b.currency === "PX") return 1;
      return b.amount - a.amount;
    });
};

/**
 * The part of a prize a person still has to send.
 *
 * Finalize credits PX by itself, so what a winner is owed by hand is everything
 * that is not PX. Asking "is there USDT?" would quietly assume USDT is the only
 * such currency, which stopped being true the moment a prize could name BTC.
 */
export const manualRewardsOf = (row) =>
  rewardsOf(row).filter((entry) => !isAutoCredited(entry.currency));

/** Does this prize owe a transfer a person has to make by hand? */
export const owesManualTransfer = (row) => manualRewardsOf(row).length > 0;

/**
 * What an empty tier is called.
 *
 * `prize_text` is NOT NULL on the API side and is derived from `rewards`, which
 * an empty list cannot do — so a tier with no amounts in it yet has to send
 * something. It reads as a real state rather than a placeholder because it is
 * one: a tier is normally added first and priced a moment later.
 */
export const EMPTY_PRIZE_TEXT = "No prize set";

/**
 * Currencies offered in the prize editors' picker.
 *
 * Not a limit — the pickers all keep an "Other" option that accepts any symbol,
 * and the API stores whatever it is sent. This is only the shortlist worth one
 * click, so that adding a BTC prize does not depend on knowing you may type
 * over what looks like a fixed label.
 *
 * PX leads because it is the one the system credits by itself; the rest are
 * roughly by how likely they are to come up.
 */
export const COMMON_CURRENCIES = [
  "PX",
  "USDT",
  "USDC",
  "BTC",
  "ETH",
  "BNB",
  "SOL",
  "TON",
  "TRX",
  "XRP",
  "DOGE",
  "LTC",
  "MATIC",
  "AVAX",
  "ADA",
  "DOT",
];

/** The picker list for a row that sits beside a dedicated PX field. */
export const COIN_CURRENCIES = COMMON_CURRENCIES.filter((currency) => currency !== "PX");
